'use client';
import React, { FC } from 'react';
import { ReactSVG } from 'react-svg';
import { hideDecorativeSvg } from '@/lib/a11y';
import Image from 'next/image';
import { motion } from 'framer-motion';
import s from './Menu.module.css';
import { Link } from '@/i18n/navigation';
import { SvgLoader, ImageLoader } from '@/lib/imageLoader';
import useIsMobile from '@/hooks/use-is-mobile';
import useLockBodyScroll from '@/hooks/use-lock-body-scroll';
import { HeaderProps } from '@/types/model';
import { listVariants, listItemVariants } from '@/lib/variants';

const Menu: FC<HeaderProps> = ({ phone, items, social, menuBackground }) => {
  const isMobile = useIsMobile();
  useLockBodyScroll();
  const bg = menuBackground?.url;

  return (
    <motion.nav className={s.menu}>
      {!isMobile && bg && (
        <motion.div
          className={s.menu__left}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.75 } }}
          exit={{ opacity: 0, transition: { duration: 0.75 } }}>
          <Image
            src={ImageLoader({ src: menuBackground?.url })}
            alt="background image"
            fill
            sizes="50vw"
            quality={65}
          />
        </motion.div>
      )}
      <motion.div
        className={s.menu__right}
        initial={{ x: 1000 }}
        animate={{ x: 0, transition: { duration: 0.7, delay: 0.5 } }}
        exit={{ x: 1000, transition: { duration: 0.7 } }}>
        {/* numero de telefono */}
        {isMobile && (
          <div className={s.menu__right__phone}>
            <ReactSVG
              src={SvgLoader({ src: phone.icon?.url })}
              className={s.phoneicon}
              wrapper="span"
              beforeInjection={hideDecorativeSvg}
            />
            <p>{phone.phoneNumber}</p>
          </div>
        )}
        <motion.ul variants={listVariants} initial="closed" animate="open">
          {items.map((item, pos) => {
            return (
              <motion.li
                key={item.id}
                variants={listItemVariants}
                initial="hidden"
                whileInView="visible"
                custom={pos}>
                <Link href={item.link} title={item.label}>
                  <span>{item.label}</span>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>
        <div className={s.menu__right__social}>
          {social.map((social) => {
            return (
              <a
                key={social.id}
                href={social.link}
                target="_blank"
                rel="noreferrer"
                aria-label={`Abrir ${social.name}`}>
                <div className={s.menu__right__social__item} key={social.id}>
                  <ReactSVG
                    src={SvgLoader({ src: social.icon?.url })}
                    className={s.socialIcon}
                    wrapper="span"
                    beforeInjection={hideDecorativeSvg}
                  />
                </div>
              </a>
            );
          })}
        </div>
      </motion.div>
    </motion.nav>
  );
};

export default Menu;
